// Plan 08-04 — PixiJS Application mount via @pixi/react v8.
//
// Per RESEARCH §"PixiJS Scene Mount via @pixi/react v8":
//   <Application> creates the PixiJS Application; children are rendered into
//   the WebGL/WebGPU canvas via the v8 reconciler. The Application accepts
//   render options (antialias, backgroundAlpha, resizeTo, preference).
//
// Provider tree (composed at the MatchScreen layer):
//   <Application>           ← @pixi/react v8 root, mounts canvas
//     <pixiContainer>       ← root container (so we can attach motion-gate hook)
//       <World playerView /> ← 5-layer scene graph
//
// `useTickerMotionGate?.()` is an optional-chain stub (Plan 04 ships a no-op
// placeholder; Plan 08 replaces with a real hook that binds
// `app.ticker.maxFPS` against the OR-union motion mode).
//
// THEME COUPLING: ThemeProvider's `app` prop will be wired by Plan 04+ once
// the canvas mounts; for now the canvas comes up unthemed and Plan 03's
// applyTheme(descriptor) sets only CSS vars. The recursive uniform binder
// becomes active when the Application instance is threaded back to the
// ThemeProvider — Plan 09 launch readiness handles that wiring (out-of-scope
// for Plan 04's scene-graph correctness).

import { Application, useApplication } from '@pixi/react'
import {
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import './pixi-extend' // side-effect: registers Container/Graphics/Sprite/Text with @pixi/react reconciler. MUST run before any <pixiGraphics>/<pixiSprite>/etc. JSX renders.
import { ensureThemeAtlasLoaded } from './assets/preloader'
import { shouldMountWorld } from './atlas-gate'
import type { BoardView } from './board-view'
import { BOARD_COLS, CELL_SIZE_PX } from './layers/board-layout'
import { ParticleSystemProvider } from './motion/ParticleSystemContext'
import { useTickerMotionGate } from './motion/ticker-gate'
import type { PlayerView } from './PlayerViewSchema'
import { useTheme } from './theme/ThemeProvider'
import { World } from './World'

interface GameCanvasProps {
  playerView: PlayerView
  boardView: BoardView
  /**
   * Display pixel WIDTH of the board wrapper. The world container is scaled by
   * `wrapperWidth / (BOARD_COLS * CELL_SIZE_PX)` so the 384-logical-px-wide
   * board fills the wrapper width exactly — keeping canvas cells the same size
   * as the DOM MatchGrid's 1fr × 1fr cells (DEC-023 1:1 alignment).
   *
   * The board is NOT square: it is 6 cols × N rows (N = 6/8/10 as the
   * Runespring field grows). We scale by WIDTH (cols are fixed) so the cell
   * size is stable as rows are appended; the wrapper's height tracks via its
   * `aspectRatio: cols/rows` (MatchScreen board-stack), so width === the
   * binding dimension and the rows stack downward without distortion.
   */
  wrapperSize: number
  /**
   * Phase 31 Plan 31-03 — optional Pixi sub-layer rendered above LetterLayer
   * inside World. Forwarded by MatchCanvas → GameCanvas → World; used by
   * AnchorPlacementOverlay to render Sun-Yellow eligible-cell highlights
   * inside the shared Pixi coordinate system.
   */
  overlayLayer?: ReactNode
}

const BOARD_LOGICAL_PX = BOARD_COLS * CELL_SIZE_PX

/** Canvas inner — mounts inside <Application> so we can use Pixi-react hooks. */
function CanvasInner({
  playerView,
  boardView,
  wrapperSize,
  overlayLayer,
}: GameCanvasProps): JSX.Element {
  // Optional-chain so Plan 08 can replace the stub without touching this file.
  useTickerMotionGate?.()
  const scale = wrapperSize > 0 ? wrapperSize / BOARD_LOGICAL_PX : 1

  // The @pixi/react <Application> initializes asynchronously (app.init()), so on
  // the first render(s) `useApplication().app` exists but `app.ticker` does NOT.
  // World's layers attach to that ticker on mount (EffectLayer's `app.ticker.add`,
  // LetterLayer/AnchorPlacementOverlay's `installTweedleTickerDriver`), so mounting
  // World before init throws "Cannot read properties of undefined (reading 'add')"
  // and the tween/particle drivers fail to attach. Gating the World mount on
  // `isInitialised` (below) guarantees a ready ticker — when init completes the
  // hook re-renders and World mounts. This races the atlas warm-up: a warm
  // (cached) atlas can flip atlasReady before init finishes, which is exactly when
  // the unguarded mount fired. Affects every board surface (match + tutorial).
  const { isInitialised } = useApplication()

  // VISUAL-PASS — warm the active theme's sprite atlas so the LetterCubeSprite
  // tiles + tier embellishments + PieceLayer piece sprites resolve REAL frame
  // textures (not Texture.EMPTY). World mount is GATED on atlasReady (see
  // shouldMountWorld) so the first paint never constructs sprites against a cold
  // cache — that flash of empty cubes (and the Pixi cache-miss warning flood) is
  // what this avoids. Degrades gracefully on load failure (atlasReady flips true
  // regardless → World mounts and sprites fall back to EMPTY).
  const { descriptor } = useTheme()
  const themeKey = descriptor?.theme_key ?? null
  const [atlasReady, setAtlasReady] = useState(false)
  useEffect(() => {
    if (!themeKey) return undefined
    let cancelled = false
    setAtlasReady(false)
    ensureThemeAtlasLoaded(themeKey)
      .then(() => {
        if (!cancelled) setAtlasReady(true)
      })
      .catch(() => {
        if (!cancelled) setAtlasReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [themeKey])

  // exactOptionalPropertyTypes-safe: only pass slot props when defined.
  // World is gated on the atlas warm-up (shouldMountWorld) so its sprites are
  // only ever constructed against a warm cache. While the atlas loads we render
  // an empty container (the wrapping chrome/background still shows). The key
  // still carries themeKey so a theme switch (which resets atlasReady) tears the
  // subtree down and reconstructs it against the new atlas.
  return (
    <pixiContainer scale={scale}>
      <ParticleSystemProvider>
        {isInitialised && shouldMountWorld(atlasReady, themeKey) ? (
          <World
            key={`world-${themeKey ?? 'none'}-${atlasReady ? 'atlas' : 'pre'}`}
            playerView={playerView}
            boardView={boardView}
            {...(overlayLayer !== undefined ? { overlayLayer } : {})}
          />
        ) : null}
      </ParticleSystemProvider>
    </pixiContainer>
  )
}

/**
 * GameCanvas — the PixiJS scene mount. Wrap with React chrome (TopBar, etc.)
 * at the MatchScreen layer; this component owns ONLY the Pixi Application
 * lifecycle.
 *
 * Render options:
 *   - antialias=true (smoother piece edges + reveal cube borders)
 *   - backgroundAlpha=0 (transparent — chrome shows through theme background)
 *   - resizeTo=<parent div> (the canvas tracks the wrapping div's box, NOT
 *     the full window — the MatchScreen is mobile-portrait and capped at
 *     ~480px wide on desktop, so sizing to window would put the board in
 *     the upper-left of an over-sized canvas).
 *   - preference="webgl" (Plan 04 ships WebGL only; Plan 09 may switch to webgpu)
 */
export function GameCanvas({
  playerView,
  boardView,
  overlayLayer,
}: Omit<GameCanvasProps, 'wrapperSize'>): JSX.Element {
  // ref-callback → state: captures the wrapping div once it mounts so we can
  // pass it as `resizeTo` to <Application>. Application is held back until
  // the parent element exists (one-frame delay; imperceptible).
  const [parent, setParent] = useState<HTMLDivElement | null>(null)
  // Track the wrapper's pixel WIDTH so the stage scale matches what the DOM
  // sees. ResizeObserver fires on every layout change (rotation, container
  // queries, viewport resize). The board is 6 cols × N rows (non-square once
  // the field grows), and COLS is fixed — so WIDTH is the binding dimension:
  // scaling by width keeps each cell a constant px size as rows are appended,
  // and the wrapper's `aspectRatio: cols/rows` makes the height grow to match.
  const [wrapperSize, setWrapperSize] = useState(0)
  // PERF-07 — track the live ResizeObserver so it is explicitly disconnected
  // when the wrapper element changes or unmounts. The previous form stashed
  // the observer on `el.__ro` and relied on GC, so the observer kept firing
  // (and held the element + setState closure alive) across the whole match
  // until the next GC sweep reclaimed it.
  const roRef = useRef<ResizeObserver | null>(null)
  const refCallback = useCallback((el: HTMLDivElement | null) => {
    setParent(el)
    if (roRef.current) {
      roRef.current.disconnect()
      roRef.current = null
    }
    if (!el) {
      setWrapperSize(0)
      return
    }
    const update = () => {
      const r = el.getBoundingClientRect()
      setWrapperSize(r.width)
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    roRef.current = ro
  }, [])
  return (
    <div
      ref={refCallback}
      style={{ width: '100%', height: '100%', position: 'relative' }}
    >
      {parent !== null ? (
        <Application
          antialias
          backgroundAlpha={0}
          preference="webgl"
          resizeTo={parent}
        >
          <CanvasInner
            playerView={playerView}
            boardView={boardView}
            wrapperSize={wrapperSize}
            {...(overlayLayer !== undefined ? { overlayLayer } : {})}
          />
        </Application>
      ) : null}
    </div>
  )
}
